import React from "react"
import styled from "styled-components"

import { PlainLink, Paragraph, ParagraphSmall } from "components/text"
import { Flex } from "components/box"

import { drugFactsHref } from "shared/products/helpers"

import { DrugTypeEnum, DosageTypeEnum } from "typings/types"
import { Product } from "typings/products"

const Link = styled(PlainLink)`
  margin-left: ${props => props.theme.space[1]};
  margin-right: ${props => props.theme.space[1]};
`

export type ExceptionProductInfo = {
  productName?: React.ReactNode
  bannerProductName?: React.ReactNode
  description?: React.ReactNode
  discount?: React.ReactNode
  btnText?: React.ReactNode
  genericName?: React.ReactNode
}

/* These products have custom JSX on the PDP pages */
export const getExceptionProductInfo = (drugType: DrugTypeEnum, product: Product): Maybe<ExceptionProductInfo> => {
  switch (drugType) {
    case DrugTypeEnum.GalcanezumabGnlm:
      return {
        productName: "Emgality®",
        bannerProductName: "Cove",
        description: (
          <>
            <Paragraph mb={5}>
              Emgality is a prescription medicine used for the preventive treatment of migraine in adults.
            </Paragraph>
            <Paragraph mb={5}>
              Once-monthly Emgality is designed to work for 30 days and nights to help prevent migraine days from
              starting in the first place.
            </Paragraph>
            <Paragraph mb={5}>
              Do not use Emgality if you are allergic to
              <Link target="_blank" href="/drug-facts/emgality-galcanezumab-gnlm" color="evergreen">
                galcanezumab-gnlm
              </Link>
              or any of the ingredients in Emgality.
              <Link href="#emgality-disclaimer" color="evergreen">
                Please see safety summary for Emgality at the bottom of the page.
              </Link>
            </Paragraph>
            <Paragraph>
              Go to
              <Link target="_blank" href="https://www.emgality.com/what-is-emgality" color="evergreen">
                Emgality.com
              </Link>
              for more information.
            </Paragraph>
          </>
        ),
        discount: "INSURANCE ACCEPTED",
        btnText: "Get started",
        genericName: "Galcanezumab-gnlm"
      }
    case DrugTypeEnum.Rimegepant:
      return {
        productName: (
          <Flex>
            Nurtec
            <ParagraphSmall bold mx={1}>
              TM
            </ParagraphSmall>
            ODT
          </Flex>
        ),
        bannerProductName: "Nurtec™",
        description: (
          <>
            <Paragraph mb={5}>
              Nurtec ODT treats migraine attacks in adults. In just one dose, it can eliminate migraine pain and get you
              back to normal function. It&apos;s the first and only orally-dissolving tablet that blocks calcitonin
              gene-related peptide (CGRP) receptors.
            </Paragraph>
            <Paragraph mb={5}>
              Nurtec ODT is not for the preventive treatment of migraine. Go to
              <Link target="_blank" href="https://www.nurtec.com/" color="evergreen">
                nurtec.com
              </Link>
              for more information.
            </Paragraph>
            <Paragraph>
              Do not take Nurtec ODT if you are allergic to Nurtec ODT (rimegepant) or any of its ingredients.
              <Link target="_blank" href={drugFactsHref(product.drugType, product.dosageType)} color="evergreen">
                More information
              </Link>
            </Paragraph>
          </>
        ),
        discount: "INSURANCE ACCEPTED",
        btnText: "Get started",
        genericName: "(Rimegepant)"
      }
    case DrugTypeEnum.Nerivio:
      return {
        productName: (
          <Flex>
            Nerivio
            <ParagraphSmall bold mx={1}>
              TM
            </ParagraphSmall>
          </Flex>
        ),
        bannerProductName: "Nerivio™",
        discount: "$10 off your first order",
        btnText: "Buy now"
      }
    case DrugTypeEnum.MigraineRelief:
      return {
        productName: <Flex>Migraine Relief 24 Ct</Flex>,
        bannerProductName: "Migraine Relief 24 Ct",
        description: <>Provides immediate pain relief during a migraine attack.</>,
        btnText: "Buy now",
        genericName: "Generic Excedrin Migraine"
      }

    case DrugTypeEnum.Diclofenac:
      return {
        productName: (
          <Flex>
            Cambia
            <ParagraphSmall bold mx={1}>
              &reg;
            </ParagraphSmall>
          </Flex>
        ),
        bannerProductName: "Cambia TM",
        description: (
          <>
            <Paragraph mb={5}>
              Cambia&reg; is an FDA-approved medication for acute migraine treatment. In as little as 15 minutes, Cambia
              may help relieve migraine pain, nausea and sensitivity to light or sound.
            </Paragraph>
            <Paragraph>
              Taking Cambia&reg; is easy: Just pour the powder into a few ounces of water and drink. Cambia’s novel
              formulation is absorbed quickly, so you get the relief you deserve.
            </Paragraph>
          </>
        ),
        btnText: "Buy now",
        genericName: "Diclofenac"
      }

    case DrugTypeEnum.Zolmitriptan:
      if (product.dosageType === DosageTypeEnum.SprayMg) {
        return {
          productName: "ZOMIG® (zolmitriptan) Nasal Spray",
          description: (
            <>
              ZOMIG Nasal Spray is a nasal spray form of zolmitriptan, and can be used for acute migraine treatment.
              ZOMIG Nasal Spray is not for the preventive treatment of migraine.
              <br />
              <br />
              Please visit{" "}
              <Link target="_blank" href="https://www.zomig.com/" color="evergreen">
                zomig.com
              </Link>{" "}
              for more information.
              <br />
              <br />
              Do not take ZOMIG Nasal Spray if you are allergic to zolmitriptan or any of the ingredients in ZOMIG Nasal
              Spray. Click{" "}
              <Link target="_blank" href="http://www.zomig.com/#isi-main" color="evergreen">
                here
              </Link>{" "}
              for more information.
            </>
          ),
          btnText: "Buy now",
          discount: "2 DAY SHIPPING"
        }
      }
  }

  return null
}

export const isExceptionProduct = (drugType: DrugTypeEnum, product: Product) =>
  !!getExceptionProductInfo(drugType, product)
